/**
 * 将后端返回的文件临时访问 URL 转为浏览器内可用的路径/请求路径。
 * 解决：accessUrl 为与当前页不同源的绝对地址时，pdf.js 内置 fetch 会触发 CORS 失败；
 * 改为只走 pathname，由当前站点（含 Vite 代理）同源访问。
 */

export function normalizeFileAccessUrlForBrowser(accessUrl: string): string {
  if (typeof window === 'undefined') return accessUrl;
  const raw = (accessUrl ?? '').trim();
  if (!raw) return raw;
  if (raw.startsWith('/')) return raw;
  try {
    const u = new URL(raw);
    const path = `${u.pathname}${u.search}`;
    if (!path.includes('/api/files/access/')) return raw;
    if (u.origin !== window.location.origin) {
      return path.startsWith('/') ? path : `/${path}`;
    }
    return raw;
  } catch {
    return raw;
  }
}

/** 供 axios（baseURL=/api）使用的相对路径，如 files/access/{token} */
export function accessUrlToApiRelativePath(accessUrl: string): string {
  const norm = normalizeFileAccessUrlForBrowser(accessUrl);
  const u = new URL(norm, typeof window !== 'undefined' ? window.location.origin : 'http://localhost');
  let p = `${u.pathname}${u.search}`;
  if (!p.includes('/files/access/')) {
    throw new Error('不是有效的脱敏文件访问地址');
  }
  if (p.startsWith('/api/')) {
    p = p.slice(5);
  }
  return p.replace(/^\//, '');
}

/** 客户下载时附加 download=1，由后端返回加密版；预览/在线评审勿使用 */
export function appendFileDownloadQuery(accessUrl: string): string {
  const raw = (accessUrl ?? '').trim();
  if (!raw) return raw;
  if (!raw.includes('/files/access/')) return raw;
  return raw.includes('?') ? `${raw}&download=1` : `${raw}?download=1`;
}

/** 文档卡片缩略图：走同源代理 + 后端 JPEG 缩略图，避免拉取原图 */
export function appendFileThumbQuery(
  accessUrl: string,
  maxWidth = 320,
  maxHeight = 120,
): string {
  const raw = normalizeFileAccessUrlForBrowser(accessUrl ?? '').trim();
  if (!raw || !raw.includes('/files/access/')) return raw;
  const params = new URLSearchParams();
  if (raw.includes('?')) {
    const u = new URL(raw, typeof window !== 'undefined' ? window.location.origin : 'http://localhost');
    u.searchParams.forEach((v, k) => params.set(k, v));
  }
  params.set('thumb', '1');
  params.set('maxWidth', String(maxWidth));
  params.set('maxHeight', String(maxHeight));
  const path = raw.split('?')[0] ?? raw;
  return `${path}?${params.toString()}`;
}

/** 预览/下载用完整图：仅做同源路径规范化 */
export function resolveFilePreviewUrl(accessUrl: string): string {
  return normalizeFileAccessUrlForBrowser(accessUrl ?? '').trim();
}
